import { useEffect, useRef, useState, useContext } from 'react';
import { MapContainer, Marker, Polyline, Popup, TileLayer } from 'react-leaflet';
import { doc, onSnapshot, collection } from 'firebase/firestore';
import * as turf from '@turf/turf';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import L from 'leaflet';
import CONFIG from '../config';
import TopHeaderMenu from '../components/TopHeaderMenu';
import CompeticionHeaderMenu from '../components/CompeticionHeaderMenu';
import { FirebaseContext } from '../index';
import { useNavigate, useParams } from 'react-router-dom';

const iconoSalida = new L.Icon({
  iconUrl: '/img/ico-salida-v2.png',
  iconSize: [22, 35],
  iconAnchor: [11, 35],
  popupAnchor: null,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null
});

const iconoMeta = new L.Icon({
  iconUrl: '/img/ico-meta-v2.png',
  iconSize: [22, 35],
  iconAnchor: [11, 35],
  popupAnchor: null,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null
});

function CompeticionGps() {
  const lineRef = useRef(null);
  const mapRef = useRef(null);
  const [evento, setEvento] = useState(null);
  const [lineCoords, setLineCoords] = useState([]);
  const [competicion, setCompeticion] = useState(null);
  const [gpsTrackers, setGpsTrackers] = useState([]);

  const { db } = useContext(FirebaseContext);
  const navigate = useNavigate();
  const params = useParams();

  function onClickBack() {
    navigate(`/competicion/${params.competicionId}`);
  }

  // Ajustar bounds del mapa cuando cambian las coordenadas
  useEffect(() => {
    if (mapRef.current && lineRef.current && lineCoords.length > 0) {
      const map = mapRef.current;
      const bounds = lineRef.current.getBounds();
      map.fitBounds(bounds);
    }
  }, [lineCoords]);

  // Cargar competición
  useEffect(() => {
    if (params.competicionId) {
      const competicionRef = doc(db, 'competicion', String(params.competicionId));

      const unlisten = onSnapshot(competicionRef, (snapshot) => {
        if (snapshot.exists()) {
          let competicion = snapshot.data();
          competicion.id = snapshot.id;

          if (competicion.mapa_geojson) {
            const geoJson = JSON.parse(competicion.mapa_geojson);
            try {
              setLineCoords(geoJson.features[0].geometry.coordinates);
            } catch (e) {
              console.trace(e);
            }
          }
          setCompeticion(competicion);
        } else {
          setCompeticion(null);
          setLineCoords([]);
        }
      });

      return unlisten;
    } else {
      setCompeticion(null);
      setLineCoords([]);
    }
  }, [params.competicionId, db]);

  // Cargar evento
  useEffect(() => {
    if (competicion && competicion.evento_id) {
      const eventoId = competicion.evento_id;

      if (eventoId) {
        const eventoRef = doc(db, 'evento', String(eventoId));

        const unlisten = onSnapshot(eventoRef, (snapshot) => {
          if (snapshot.exists()) {
            let evento = snapshot.data();
            evento.id = snapshot.id;
            setEvento(evento);
          } else {
            setEvento(null);
          }
        });

        return unlisten;
      }
    } else {
      setEvento(null);
    }
  }, [competicion, db]);

  // Cargar GPS trackers
  useEffect(() => {
    if (competicion && competicion.trackers && lineCoords.length > 0) {
      const activeTrackers = {};
      competicion.trackers.forEach(t => {
        activeTrackers[t.imei] = t;
      });
      const activeImeis = Object.keys(activeTrackers);
      const turfLine = turf.lineString(lineCoords);

      const gpsCollectionRef = collection(db, 'gps');

      const unlisten = onSnapshot(gpsCollectionRef, (snapshot) => {
        let _trackers = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(t => activeImeis.includes(t.id))
          .map(t => {
            // Buscar punto más cercano en la línea
            const point = turf.point([t.lng, t.lat]);
            const cercano = nearestPointOnLine(turfLine, point, { units: 'kilometers' });

            const icono = new L.Icon({
              iconUrl: '/img/gps/' + activeTrackers[t.id].icono,
              iconSize: [70, 70],
              iconAnchor: [35, 70],
              popupAnchor: [0, -70],
              shadowUrl: null,
              shadowSize: null,
              shadowAnchor: null
            });

            return {
              id: t.id,
              ...activeTrackers[t.id],
              ...t,
              cercano,
              icono
            };
          });

        setGpsTrackers(_trackers);
      });

      return unlisten;
    }
  }, [competicion, lineCoords, db]);

  return (
    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}>
      <TopHeaderMenu
        onClickBack={onClickBack}
        competicion={competicion}
        evento={evento}
      />
      <CompeticionHeaderMenu
        competicion={competicion}
        selected={5}
        evento={evento}
      />
      <MapContainer
        ref={mapRef}
        boundsOptions={{ padding: [0, 0] }}
        style={{ position: 'absolute', top: '176px', margin: 0, bottom: 0, padding: 0 }}
        center={[51.505, -0.09]}
        zoom={13}
        scrollWheelZoom={false}
      >
        <TileLayer url={CONFIG.MAP_LAYER} />

        <Polyline
          ref={lineRef}
          color={CONFIG.PRIMARY_COLOR}
          positions={lineCoords.map(r => [r[1], r[0]])}
        />

        {(lineCoords && lineCoords.length) && (
          <>
            <Marker position={[lineCoords[0][1], lineCoords[0][0]]} icon={iconoSalida} />
            <Marker
              position={[lineCoords[lineCoords.length - 1][1], lineCoords[lineCoords.length - 1][0]]}
              icon={iconoMeta}
            />
          </>
        )}

        {gpsTrackers.map((row) => {
          if (row.cercano && row.cercano.properties.dist < 0.5) {
            return (
              <Marker
                key={row.id}
                icon={row.icono}
                position={[row.cercano.geometry.coordinates[1], row.cercano.geometry.coordinates[0]]}
              />
            );
          } else {
            return (
              <Marker
                key={row.id}
                icon={row.icono}
                position={[lineCoords[0][1], lineCoords[0][0]]}
              >
                <Popup>
                  <b>{row.nombre}</b>
                </Popup>
              </Marker>
            );
          }
        })}
      </MapContainer>
    </div>
  );
}

export default CompeticionGps;